import axios from "axios";
import Format from "string-format";
import {
  ADD_NEW_TEAM_MEMBER,
  DELETE_MEMBER,
  ROLES_ENDPOINT,
  SHOW_MY_TEAM_MEMBERS,
  UPDATE_WORKSPACE_USER_ROLE,
} from "../../_actions/endpoints";
import { useDispatch, useSelector } from "react-redux";
import {
  analyticsTrackEvent,
  getAuthToken,
  isAuthorizeToChangeWorkspaceUserRole,
  isFeatureAvailable,
} from "../../_utils/common";
import * as WorkflowTypes from "../../_constants/WorkflowTypes";

import { findIndex } from "lodash";
import OrgMembers from "../settings/OrgMembers";
import UserInviteForm from "../settings/UserInviteForm";
import workflowArtBoard from "../../assets/images/workflowArtBoard.jpeg";

import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  List,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { showToast } from "../../_utils/showToast";
import { handleHTTPError } from "../../_actions/errorHandlerActions";
import { switchWorkspace, syncWorkspaces } from "../../_actions/authActions";
import { useEffect, useState } from "react";
import {
  fetchPlannerWorkflowType,
  updateWorkflowType,
} from "../../_actions/plannerActions";
import { BILLING_PLAN_GROWTH_PLAN, FeatureCodes } from "../details/constants";
import connectedFeatureAvailabilityWrapper from "./withFeatureAvailability";
import { INVITE_USER_TO_WORKSPACE_VALIDATION_SCHEMA } from "../../_validation/inviteUserToWorkspace";
import TldrTrialEndedModal from "../../TldrSettings/TldrBillingAndPayments/Modals/TldrTrialEndedModal";
import FeatureLockModal from "../featureLockModal/FeatureLockModal";
import { FaComment, FaLock, FaUsers } from "react-icons/fa";
import isEmpty from "../../_validation/is-empty";
import { isFreePlanOrTrialEnded } from "../../_utils/isFreePlan";

const TldrWorkspaceAddMembersModal = (props) => {
  const { t } = useTranslation(["workspace"]);
  const [roles, setRoles] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [isApprovalWorkflowModalOpen, setIsApprovalWorkflowModalOpen] =
    useState(false);
  const [isApprovalWorkflowAvailable, setIsApprovalWorkflowAvailable] =
    useState(false);
  const [showUpgradeModalForApproval, setShowUpgradeModalForApproval] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const subscription = useSelector((state) => state.subscription);

  const authenticatedUserId = auth?.payload?.user?.pk;
  const { subscribedPlan } = subscription;
  const { plan } = subscribedPlan;

  const handleEnableWorkflow = async () => {
    setLoading(true);
    try {
      await updateWorkflowType("advanced");
    } catch {
      // handled in action;
    }

    analyticsTrackEvent("enableWorkflow", {
      location: "settings",
    });

    setLoading(false);
    setIsApprovalWorkflowModalOpen(false);
  };

  const handleUpgradePlan = () => {
    setShowUpgradeModalForApproval(true);
    setShowUpgradePlanModal(true);
  };

  const checkRoleRestrictions = async (roleId) => {
    const roleName = roles.find((r) => r.id === roleId).name;

    if (["workspace_creator", "workspace_approver"].includes(roleName)) {
      if (
        isFeatureAvailable(
          subscription,
          FeatureCodes.PLANNER_WORKFLOW_APPROVAL,
          false
        )
      ) {
        setIsApprovalWorkflowAvailable(true);
      }

      const workflowType = await fetchPlannerWorkflowType();

      if (workflowType === WorkflowTypes.SIMPLE) {
        setIsApprovalWorkflowModalOpen(true);

        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      const roleCategoryName = "workspace";
      fetchRoles(roleCategoryName);
      fetchOrgMembers();
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchRoles = (roleCategoryName = "") => {
    const categoryName = "workspace";
    const GET_ROLES_ENDPOINT = roleCategoryName
      ? `${ROLES_ENDPOINT}?category__name=${categoryName}`
      : ROLES_ENDPOINT;
    axios.get(GET_ROLES_ENDPOINT).then((response) => {
      const roles = response.data.results;
      let userRoles = roles.map((role) => ({
        ...role,
        value: role.name,
        label: role.display_name, // TODO: role.displayName
      }));

      setRoles(userRoles);
    });
  };

  const fetchOrgMembers = () => {
    const currentOrg = auth.payload.selectedOrg;
    axios
      .get(`${SHOW_MY_TEAM_MEMBERS}${currentOrg}/members`)
      .then((response) => {
        const orgMembers = response.data.results;

        setOrgMembers(orgMembers);
      })
      .catch((error) => {
        handleHTTPError(error);
      });
  };

  const onClose = () => {
    setConfirmation(false);
  };
  const onConfirmation = (
    formValues,
    { setSubmitting, resetForm, setErrors }
  ) => {
    props.checkFeatureAvailability(FeatureCodes.INVITE_MEMBERS, () => {
      sendInvite(formValues, {
        setSubmitting,
        resetForm,
        setErrors,
      });
    });
    setConfirmation(false);
  };

  const addMembers = (endpoint, data) => {
    return axios.post(endpoint, data);
  };

  const sendInvite = async (
    formValues,
    { setSubmitting, resetForm, setErrors }
  ) => {
    const { payload } = auth;

    const isInviteMemberAvailable = props.checkFeatureAvailability(
      FeatureCodes.INVITE_MEMBERS,
      () => {
        // setSubmitting(false);
      }
    );

    if (!isInviteMemberAvailable) {
      setSubmitting(false);
      return;
    }

    const roleChecked = await checkRoleRestrictions(formValues.role);

    if (!roleChecked) {
      setSubmitting(false);

      return;
    }

    // convert email address into lowercase
    if (formValues?.email) {
      formValues.email = formValues.email.toLowerCase();
    }
    return addMembers(
      `${ADD_NEW_TEAM_MEMBER}${payload.selectedOrg}/members`,
      formValues
    )
      .then((response) => {
        setOrgMembers(orgMembers.concat(response.data));
        showToast({
          description: t("success.add_members.message"),
          title: t("success.add_members.heading"),
          status: "success",
        });

        analyticsTrackEvent("inviteMembers", {
          location: "settings",
        });
        setSubmitting(false);
        resetForm();
        dispatch(syncWorkspaces());
      })
      .catch((error) => {
        setErrors({ email: error.response.data["email"]?.[0] });
        setSubmitting(false);
        const errorMessage = error.response.data["email"]?.[0];
        showToast({
          description: errorMessage || t("failure.add_members.message"),
          title: t("failure.add_members.heading"),
          status: "error",
        });
      });
  };

  const onChangeWorkspaceMemberRole = (member, role) => {
    updateMemberRole(member, role)
      .then((response) => {
        const updatedInfo = response.data;
        let currentMembers = orgMembers;

        const userIndex = findIndex(
          currentMembers,
          (object) => object.id === updatedInfo.id
        );

        if (userIndex > -1) {
          currentMembers[userIndex] = updatedInfo;

          setOrgMembers([...currentMembers]);
        }
        showToast({
          description: t("success.role_change.message", {
            role: updatedInfo.role.display_name,
          }),
          title: t("success.role_change.heading"),
          status: "success",
        });
      })
      .catch((error) => {
        handleHTTPError(error);
      });
  };

  const onRemind = (member, role) => {
    const { payload } = auth;
    const userData = {
      email: member.email,
    };
    addMembers(
      `${ADD_NEW_TEAM_MEMBER}${payload.selectedOrg}/members?remind_me=true`,
      userData
    )
      .then((response) => {
        showToast({
          description: t("success.reminder.message"),
          title: t("success.reminder.heading"),
          status: "success",
        });
      })
      .catch((error) => {
        handleHTTPError(error);
      });
  };

  const removeMember = (member) => {
    const { payload } = auth;
    return axios.delete(Format(DELETE_MEMBER, payload.selectedOrg, member.id));
  };

  const onRemoveWorkspaceMember = (member, role) => {
    removeMember(member)
      .then((res) => {
        setOrgMembers(
          orgMembers.filter((_member) => {
            return _member.id !== member.id;
          })
        );
        showToast({
          description: t("success.remove_members.message"),
          title: t("success.remove_members.heading"),
          status: "success",
        });
        dispatch(syncWorkspaces());
      })
      .catch((error) => {
        showToast({
          description: t("failure.remove.message"),
          title: t("failure.remove.heading"),
          status: "error",
        });
        handleHTTPError(error);
      });
  };

  const onLeaveWorkspace = (member, role) => {
    removeMember(member)
      .then((res) => {
        showToast({
          description: t("success.leave_workspace.message"),
          title: t("success.leave_workspace.heading"),
          status: "success",
        });
        const foundOrgId = auth.payload?.orgs.find(
          (org) => org.id !== auth.payload?.selectedOrg
        )?.id;

        dispatch(
          switchWorkspace(foundOrgId, getAuthToken()).then(() => {
            window.location.reload(false);
          })
        );
      })
      .catch((error) => {
        showToast({
          description: t("failure.remove.message"),
          title: t("failure.remove.heading"),
          status: "error",
        });
        handleHTTPError(error);
      });
  };

  const updateMemberRole = async (member, role) => {
    const { payload } = auth;
    const roleChecked = await checkRoleRestrictions(role.id);

    if (!roleChecked) {
      throw new Error();
    }

    const requestBody = {
      role: role.id,
    };

    const res = await axios.put(
      Format(`${UPDATE_WORKSPACE_USER_ROLE}`, payload.selectedOrg, member.id),
      requestBody
    );

    return res;
  };

  const onOrgMemberAction = (action, actionData) => {
    const { role, member } = actionData;
    switch (action) {
      case "changeRole":
        onChangeWorkspaceMemberRole(member, role);
        break;
      case "remind":
        onRemind(member, role);
        break;
      case "remove":
        onRemoveWorkspaceMember(member, role);
        break;
      case "leave":
        onLeaveWorkspace(member, role);
        break;
      default:
        return;
    }
  };

  return (
    <div>
      <Modal isOpen={true} onClose={props.onHide} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader p="4">
            <HStack alignItems="center" justify="space-between">
              <Text>
                {t("members", {
                  total_members: orgMembers.length,
                  available_seats: subscription.available_seats,
                })}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <Divider my="0" />

          <ModalBody
            px="4"
            pt={0}
            pb={4}
            minH={"32"}
            maxH="96"
            overflowY="auto"
          >
            <Box>
              <>
                <UserInviteForm
                  onClose={onClose}
                  onConfirmation={onConfirmation}
                  confirmation={confirmation}
                  roles={roles}
                  onInvite={(
                    values,
                    { setSubmitting, resetForm, setErrors }
                  ) => {
                    props.checkFeatureAvailability(
                      FeatureCodes.INVITE_MEMBERS,
                      () => {
                        if (
                          isFreePlanOrTrialEnded(subscription?.subscribedPlan)
                        ) {
                          setShowUpgradePlanModal(true);
                          setSubmitting(false);
                          resetForm();
                        } else if (
                          orgMembers.length < subscription.available_seats ||
                          subscribedPlan?.trial_remaining_days > 0
                        ) {
                          // directly add the member without confirmation when workspace has more available seats that members
                          sendInvite(values, {
                            setSubmitting,
                            resetForm,
                            setErrors,
                          });
                        } else {
                          // show confirmation modal in case of an active plan, this action will charge user for every new member
                          setConfirmation(true);
                        }
                      }
                    );
                  }}
                  validationSchema={INVITE_USER_TO_WORKSPACE_VALIDATION_SCHEMA}
                  helperText={
                    plan
                      ? t("cost_info", {
                          amount: plan?.amount,
                          interval: plan?.interval,
                        })
                      : ""
                  }
                  initialValues={{
                    email: "",
                    role: roles?.length > 0 && roles[0].id,
                  }}
                />
                {showUpgradePlanModal && (
                  <TldrTrialEndedModal
                    selectedPlanName={
                      showUpgradeModalForApproval
                        ? BILLING_PLAN_GROWTH_PLAN
                        : null
                    }
                    show={showUpgradePlanModal}
                    onHide={() => {
                      setShowUpgradePlanModal(false);
                      setConfirmation(false);
                    }}
                  />
                )}
              </>
              {!isEmpty(orgMembers) ? (
                <Box>
                  <OrgMembers
                    roles={roles}
                    members={orgMembers}
                    rolePrefix={"Make "}
                    isAuthorizeToChangeRole={isAuthorizeToChangeWorkspaceUserRole(
                      authenticatedUserId,
                      orgMembers
                    )}
                    onAction={onOrgMemberAction}
                  />
                </Box>
              ) : (
                <Center>
                  <Spinner size={"xl"} color="brand.primary" />
                </Center>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      {isApprovalWorkflowModalOpen && (
        <FeatureLockModal
          confirmButtonText={
            isApprovalWorkflowAvailable
              ? t("enable_now", { ns: "common" })
              : t("choose_plan", { ns: "common" })
          }
          video="https://www.youtube.com/embed/t-mCMW5DlaI"
          image={workflowArtBoard}
          loading={loading}
          onCancel={() => setIsApprovalWorkflowModalOpen(false)}
          onConfirm={
            isApprovalWorkflowAvailable
              ? handleEnableWorkflow
              : handleUpgradePlan
          }
        >
          <Box flex="1">
            <Heading size="md" mb="3" color="white">
              {isApprovalWorkflowAvailable
                ? t("workflow_heading_role", { ns: "planner" })
                : t("workflow_heading_role_unavailable", { ns: "planner" })}
            </Heading>
            <Text mb="8" color="gray.100" fontSize="sm">
              {t("workflow_description", { ns: "planner" })}&nbsp;
              <Link
                href="https://help.simplified.co/en/collections/3342361-simplified-content-calendar"
                isExternal
              >
                {t("learn_more", { ns: "common" })}
              </Link>
              .
            </Text>
            <List spacing="4" mb="4">
              <ListItem>
                <Flex>
                  <Icon
                    as={FaLock}
                    color="brand.primary"
                    fontSize="2xl"
                    mr="3"
                  />
                  <Box>
                    <Heading
                      size="md"
                      fontSize="md"
                      mb="1"
                      as="h3"
                      color="white"
                    >
                      {t("workflow_feature_1", { ns: "planner" })}
                    </Heading>
                    <Text color="gray.100" fontSize="sm">
                      {t("workflow_feature_1_description", {
                        ns: "planner",
                      })}
                    </Text>
                  </Box>
                </Flex>
              </ListItem>
              <ListItem>
                <Flex>
                  <Icon
                    as={FaUsers}
                    color="brand.primary"
                    fontSize="2xl"
                    mr="3"
                  />
                  <Box>
                    <Heading
                      size="md"
                      fontSize="md"
                      mb="1"
                      as="h3"
                      color="white"
                    >
                      {t("workflow_feature_2", { ns: "planner" })}
                    </Heading>
                    <Text color="gray.100" fontSize="sm">
                      {t("workflow_feature_2_description", {
                        ns: "planner",
                      })}
                    </Text>
                  </Box>
                </Flex>
              </ListItem>
              <ListItem>
                <Flex>
                  <Icon
                    as={FaComment}
                    color="brand.primary"
                    fontSize="2xl"
                    mr="3"
                  />
                  <Box>
                    <Heading
                      size="md"
                      fontSize="md"
                      mb="1"
                      as="h3"
                      color="white"
                    >
                      {t("workflow_feature_3", { ns: "planner" })}
                    </Heading>
                    <Text color="gray.100" fontSize="sm">
                      {t("workflow_feature_3_description", {
                        ns: "planner",
                      })}
                    </Text>
                  </Box>
                </Flex>
              </ListItem>
            </List>
          </Box>
        </FeatureLockModal>
      )}
    </div>
  );
};

export default connectedFeatureAvailabilityWrapper(
  TldrWorkspaceAddMembersModal
);
